export const FISHYMAP_CHARACTER_INPUT_VERSION = 1;
export const FISHYMAP_CHARACTER_INPUT_LIMIT = 64;

const MAX_IDENTITY_TEXT_LENGTH = 128;
const MAX_NAME_TEXT_LENGTH = 64;
const MAX_REGION_TEXT_LENGTH = 16;
const MAX_POSITION_CANDIDATES = 3;
const NAMES_STATES = new Set(["available", "withheld", "unavailable"]);
const SUMMARY_STATES = new Set(["complete", "unavailable"]);
const PRESENCE_STATUSES = new Set(["live", "stale"]);
const POSITION_EVIDENCE = new Set([
  "contextDependentActorPosition",
  "worldCoordinateCandidate",
  "fishingLookupXzV1",
]);
const OBSERVATION_EVIDENCE = new Set([
  "sourceObservedLocalSession",
  "sourceObservedBinding",
  "localProcessSessionAttribution",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasOwn(value, key) {
  return Object.prototype.hasOwnProperty.call(value, key);
}

function assertObject(value, field) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${field} must be an object`);
  }
}

function assertOnlyKeys(value, allowed, field) {
  const allowedKeys = new Set(allowed);
  const unsupported = Object.keys(value).find((key) => !allowedKeys.has(key));
  if (unsupported) {
    throw new TypeError(`${field} contains unsupported field ${unsupported}`);
  }
}

function requiredText(value, field, maxLength) {
  if (
    typeof value !== "string" ||
    !value ||
    value.trim() !== value ||
    [...value].length > maxLength
  ) {
    throw new TypeError(`${field} must be non-empty, trimmed, and at most ${maxLength} characters`);
  }
  return value;
}

function decimalU64(value, field) {
  if (
    typeof value !== "string" ||
    !/^(0|[1-9][0-9]{0,19})$/.test(value)
  ) {
    throw new TypeError(`${field} must be a canonical decimal u64 string`);
  }
  const parsed = BigInt(value);
  if (parsed > 18_446_744_073_709_551_615n) {
    throw new TypeError(`${field} exceeds u64`);
  }
  return value;
}

function safeNonNegativeInteger(value, field) {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new TypeError(`${field} must be a non-negative safe integer`);
  }
  return value;
}

function unsigned32(value, field) {
  if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
    throw new TypeError(`${field} must be an unsigned 32-bit integer`);
  }
  return value;
}

function positiveUnsigned16(value, field) {
  if (!Number.isInteger(value) || value <= 0 || value > 0xffff) {
    throw new TypeError(`${field} must be a positive unsigned 16-bit integer`);
  }
  return value;
}

function normalizeHeader(value, field) {
  assertObject(value, field);
  if (value.version !== FISHYMAP_CHARACTER_INPUT_VERSION) {
    throw new TypeError(
      `${field}.version must be ${FISHYMAP_CHARACTER_INPUT_VERSION}`,
    );
  }
  const revision = safeNonNegativeInteger(value.revision, `${field}.revision`);
  if (!Array.isArray(value.characters)) {
    throw new TypeError(`${field}.characters must be an array`);
  }
  if (value.characters.length > FISHYMAP_CHARACTER_INPUT_LIMIT) {
    throw new TypeError(
      `${field}.characters exceeds ${FISHYMAP_CHARACTER_INPUT_LIMIT} entries`,
    );
  }
  return { revision, characters: value.characters };
}

function identityKey(value, field) {
  return requiredText(value, field, MAX_IDENTITY_TEXT_LENGTH);
}

export function normalizeCharacterIdentityKey(value, field = "characterIdentityKey") {
  return identityKey(value, field);
}

function normalizeCatalogSession(value, field) {
  assertObject(value, field);
  assertOnlyKeys(value, ["sessionKey", "startedAtNs", "observationEvidence"], field);
  if (!OBSERVATION_EVIDENCE.has(value.observationEvidence)) {
    throw new TypeError(`${field}.observationEvidence is unsupported`);
  }
  return {
    sessionKey: identityKey(value.sessionKey, `${field}.sessionKey`),
    startedAtNs: decimalU64(value.startedAtNs, `${field}.startedAtNs`),
    observationEvidence: value.observationEvidence,
  };
}

function normalizeNames(value, field) {
  assertObject(value, field);
  assertOnlyKeys(value, ["state", "familyName", "characterName"], field);
  if (!NAMES_STATES.has(value.state)) {
    throw new TypeError(`${field}.state is unsupported`);
  }
  if (value.state === "available") {
    return {
      state: value.state,
      familyName: requiredText(value.familyName, `${field}.familyName`, MAX_NAME_TEXT_LENGTH),
      characterName: requiredText(
        value.characterName,
        `${field}.characterName`,
        MAX_NAME_TEXT_LENGTH,
      ),
    };
  }
  if (hasOwn(value, "familyName") || hasOwn(value, "characterName")) {
    throw new TypeError(`${field} must not contain names when ${value.state}`);
  }
  return { state: value.state };
}

function normalizeGearSummary(value, field) {
  assertObject(value, field);
  assertOnlyKeys(value, ["state", "totalSlotCount", "occupiedSlotCount"], field);
  if (!SUMMARY_STATES.has(value.state)) {
    throw new TypeError(`${field}.state is unsupported`);
  }
  if (value.state === "complete") {
    const totalSlotCount = unsigned32(value.totalSlotCount, `${field}.totalSlotCount`);
    const occupiedSlotCount = unsigned32(
      value.occupiedSlotCount,
      `${field}.occupiedSlotCount`,
    );
    if (occupiedSlotCount > totalSlotCount) {
      throw new TypeError(`${field}.occupiedSlotCount exceeds totalSlotCount`);
    }
    return { state: value.state, totalSlotCount, occupiedSlotCount };
  }
  if (hasOwn(value, "totalSlotCount") || hasOwn(value, "occupiedSlotCount")) {
    throw new TypeError(`${field} must not contain counts when unavailable`);
  }
  return { state: value.state };
}

function normalizeFishGuideSummary(value, field) {
  assertObject(value, field);
  assertOnlyKeys(value, ["state", "speciesCount", "totalCatchCount"], field);
  if (!SUMMARY_STATES.has(value.state)) {
    throw new TypeError(`${field}.state is unsupported`);
  }
  if (value.state === "complete") {
    return {
      state: value.state,
      speciesCount: unsigned32(value.speciesCount, `${field}.speciesCount`),
      totalCatchCount: safeNonNegativeInteger(
        value.totalCatchCount,
        `${field}.totalCatchCount`,
      ),
    };
  }
  if (hasOwn(value, "speciesCount") || hasOwn(value, "totalCatchCount")) {
    throw new TypeError(`${field} must not contain counts when unavailable`);
  }
  return { state: value.state };
}

function normalizeCatalogEntry(value, index) {
  const field = `characterCatalog.characters[${index}]`;
  assertObject(value, field);
  assertOnlyKeys(
    value,
    [
      "identityKey",
      "serviceRegion",
      "session",
      "characterClassKey",
      "names",
      "gearSummary",
      "fishGuideSummary",
    ],
    field,
  );
  const out = {
    identityKey: identityKey(value.identityKey, `${field}.identityKey`),
    names: normalizeNames(value.names, `${field}.names`),
    gearSummary: normalizeGearSummary(value.gearSummary, `${field}.gearSummary`),
    fishGuideSummary: normalizeFishGuideSummary(
      value.fishGuideSummary,
      `${field}.fishGuideSummary`,
    ),
  };
  if (hasOwn(value, "serviceRegion")) {
    out.serviceRegion = requiredText(
      value.serviceRegion,
      `${field}.serviceRegion`,
      MAX_REGION_TEXT_LENGTH,
    );
  }
  if (hasOwn(value, "session")) {
    out.session = normalizeCatalogSession(value.session, `${field}.session`);
  }
  if (hasOwn(value, "characterClassKey")) {
    out.characterClassKey = positiveUnsigned16(
      value.characterClassKey,
      `${field}.characterClassKey`,
    );
  }
  return out;
}

export function normalizeCharacterCatalogSnapshot(value) {
  assertObject(value, "characterCatalog");
  assertOnlyKeys(value, ["version", "revision", "characters"], "characterCatalog");
  const { revision, characters } = normalizeHeader(value, "characterCatalog");
  const identities = new Set();
  const normalizedCharacters = characters.map((entry, index) => {
    const normalized = normalizeCatalogEntry(entry, index);
    if (identities.has(normalized.identityKey)) {
      throw new TypeError(`characterCatalog contains duplicate identityKey ${normalized.identityKey}`);
    }
    identities.add(normalized.identityKey);
    return normalized;
  });
  return {
    version: FISHYMAP_CHARACTER_INPUT_VERSION,
    revision,
    characters: normalizedCharacters,
  };
}

function normalizePositionCandidate(value, field) {
  assertObject(value, field);
  assertOnlyKeys(value, ["x", "z", "evidence"], field);
  if (!Number.isFinite(value.x) || !Number.isFinite(value.z)) {
    throw new TypeError(`${field} coordinates must be finite numbers`);
  }
  if (!POSITION_EVIDENCE.has(value.evidence)) {
    throw new TypeError(`${field}.evidence is unsupported`);
  }
  return { x: value.x, z: value.z, evidence: value.evidence };
}

function normalizePresenceEntry(value, index) {
  const field = `characterPresence.characters[${index}]`;
  assertObject(value, field);
  assertOnlyKeys(
    value,
    [
      "identityKey",
      "sessionKey",
      "status",
      "observedAtNs",
      "horizontalPositionCandidates",
    ],
    field,
  );
  if (!PRESENCE_STATUSES.has(value.status)) {
    throw new TypeError(`${field}.status is unsupported`);
  }
  if (!Array.isArray(value.horizontalPositionCandidates)) {
    throw new TypeError(`${field}.horizontalPositionCandidates must be an array`);
  }
  if (value.horizontalPositionCandidates.length > MAX_POSITION_CANDIDATES) {
    throw new TypeError(
      `${field}.horizontalPositionCandidates exceeds ${MAX_POSITION_CANDIDATES} entries`,
    );
  }
  const evidence = new Set();
  const horizontalPositionCandidates = value.horizontalPositionCandidates.map(
    (candidate, candidateIndex) => {
      const normalized = normalizePositionCandidate(
        candidate,
        `${field}.horizontalPositionCandidates[${candidateIndex}]`,
      );
      if (evidence.has(normalized.evidence)) {
        throw new TypeError(`${field} contains duplicate evidence ${normalized.evidence}`);
      }
      evidence.add(normalized.evidence);
      return normalized;
    },
  );
  return {
    identityKey: identityKey(value.identityKey, `${field}.identityKey`),
    sessionKey: identityKey(value.sessionKey, `${field}.sessionKey`),
    status: value.status,
    observedAtNs: decimalU64(value.observedAtNs, `${field}.observedAtNs`),
    horizontalPositionCandidates,
  };
}

export function normalizeCharacterPresenceSnapshot(value) {
  assertObject(value, "characterPresence");
  assertOnlyKeys(value, ["version", "revision", "characters"], "characterPresence");
  const { revision, characters } = normalizeHeader(value, "characterPresence");
  const identities = new Set();
  const normalizedCharacters = characters.map((entry, index) => {
    const normalized = normalizePresenceEntry(entry, index);
    if (identities.has(normalized.identityKey)) {
      throw new TypeError(
        `characterPresence contains duplicate identityKey ${normalized.identityKey}`,
      );
    }
    identities.add(normalized.identityKey);
    return normalized;
  });
  return {
    version: FISHYMAP_CHARACTER_INPUT_VERSION,
    revision,
    characters: normalizedCharacters,
  };
}

export function emptyCharacterCatalogSnapshot() {
  return { version: FISHYMAP_CHARACTER_INPUT_VERSION, revision: 0, characters: [] };
}

export function emptyCharacterPresenceSnapshot() {
  return { version: FISHYMAP_CHARACTER_INPUT_VERSION, revision: 0, characters: [] };
}
